// Creative NodeFlow - Application Constants
// Contains all magic numbers, strings, and configuration values

// Timing constants
export const TIMING = {
  HANDLER_REGISTRATION_DELAY: 100, // ms - delay for registering node handlers
  FOCUS_DELAY: 0, // ms - delay before focusing textarea
  DEBOUNCE_DELAY: 300, // ms - general debounce delay
} as const;

// Limits and constraints
export const LIMITS = {
  MAX_CONTEXT_MESSAGES: 5, // Maximum messages to keep in conversation context (reduced from 20 to save tokens)
  PREVIEW_TEXT_LENGTH: 80, // Characters to show in preview
  PREVIEW_TEXT_LENGTH_LONG: 100, // Characters for longer previews
  MAX_PROMPT_LENGTH: 10000, // Maximum allowed prompt length
  MAX_TOKENS: 128000, // Maximum output tokens for GPT-5-nano (128K tokens, context window: 400K)
} as const;

// API Models
export const MODELS = {
  OPENAI: 'gpt-5-nano',
  OPENAI_MINI: 'gpt-5-mini',
  OPENAI_STANDARD: 'gpt-5',
  OPENAI_PRO: 'gpt-5.2',
  OPENAI_VISION: 'gpt-5-nano', // GPT-5-nano supports both text and vision
  // GPT-5.4 series (March 2026)
  OPENAI_5_4_NANO: 'gpt-5.4-nano',
  OPENAI_5_4: 'gpt-5.4',
  OPENAI_5_4_PRO: 'gpt-5.4-pro',
  // GPT-5.4 mini — fills the gap between the $0.20 nano and the $1.00 luna
  OPENAI_5_4_MINI: 'gpt-5.4-mini',
  // GPT-5.5 series (April 2026)
  OPENAI_5_5: 'gpt-5.5',
  OPENAI_5_5_PRO: 'gpt-5.5-pro',
  // GPT-5.6 series (July 2026) — current generation, 1.05M context
  OPENAI_5_6_SOL: 'gpt-5.6-sol',     // flagship, $5 / $30 per MTok
  OPENAI_5_6_TERRA: 'gpt-5.6-terra', // balanced, $2.50 / $15
  OPENAI_5_6_LUNA: 'gpt-5.6-luna',   // fast + cheap, $1 / $6
  // NOTE: do NOT repoint OPENAI / OPENAI_VISION above to a newer id. Those
  // constants double as MODEL_REGISTRY keys, so changing their value renames a
  // registry entry that saved flows still reference. Retiring a model means
  // removing it from a node's `models: []` list, not repointing the constant.
  GOOGLE_IMAGE: 'gemini-2.5-flash-image', // "Nano Banana" - Stable version
  GOOGLE_IMAGE_PRO: 'gemini-3-pro-image-preview', // "Nano Banana Pro" - Premium quality (preview)
  GOOGLE_IMAGE_2: 'gemini-3.1-flash-image', // "Nano Banana 2" - Speed-optimized (preview)
  GOOGLE_IMAGE_2_LITE: 'gemini-3.1-flash-lite-image', // "Nano Banana 2 Lite" - fastest/cheapest, 1K only
  GOOGLE_TEXT: 'gemini-2.5-flash',
  GOOGLE_VIDEO: 'veo-3.1-fast-generate-preview', // Veo 3.1 Fast (Preview) - latest features. Stable: veo-3.1-fast-generate-001
  // Gemini Text models (Google AI direct)
  GEMINI_3_5_FLASH: 'gemini-3.5-flash',
  GEMINI_3_1_PRO: 'gemini-3.1-pro-preview',
  GEMINI_3_FLASH: 'gemini-3-flash-preview',
  GEMINI_3_1_FLASH_LITE: 'gemini-3.1-flash-lite',
} as const;

// API Error Messages
export const API_ERRORS = {
  OPENAI_NOT_CONFIGURED: 'OpenAI API key not configured. Please check your .env file and ensure REACT_APP_OPENAI_API_KEY is set.',
  GOOGLE_NOT_CONFIGURED: 'Google API key not configured. Please check your .env file and ensure REACT_APP_GOOGLE_API_KEY is set.',
  CLIENT_NOT_INITIALIZED: 'AI client not initialized. Please check your API key configuration.',
  BILLING_REQUIRED: 'Image generation requires billing to be enabled on your Google Cloud account.',
  RATE_LIMIT: 'Rate limit exceeded. Please wait before making another request.',
  INVALID_REQUEST: 'Invalid request. Please check your input and try again.',
} as const;

// Node Types
export const NODE_TYPES = {
  STARTING_PROMPT: 'startingPrompt',
  AGENT_PROMPT: 'agentPrompt',
  IMAGE_PROMPT: 'imagePrompt',
  CUSTOM_OUTPUT: 'customOutput',
} as const;

export type NodeType = typeof NODE_TYPES[keyof typeof NODE_TYPES];

// Default positions for new nodes
export const DEFAULT_POSITIONS = {
  STARTING_PROMPT: { x: 250, y: 100 },
  AGENT_PROMPT: { x: 250, y: 250 },
  IMAGE_PROMPT: { x: 250, y: 400 },
  OUTPUT: { x: 500, y: 200 },
} as const;

// CSS Classes
export const CSS_CLASSES = {
  CONNECTED_INPUT: 'connected-input',
  CONNECTED_OUTPUT: 'connected-output',
  PROCESSING: 'processing',
  ERROR: 'error',
} as const;

// Default system prompts
// The DEFAULT prompt reads from environment variable with a fallback
// Set REACT_APP_DEFAULT_SYSTEM_PROMPT in .env to customize per environment
const ENV_SYSTEM_PROMPT = typeof process !== 'undefined' && process.env?.REACT_APP_DEFAULT_SYSTEM_PROMPT;
const FALLBACK_SYSTEM_PROMPT = 'You are a helpful AI assistant. Provide direct answers only—no preamble ("Sure, here is..."), no closing remarks ("Let me know if...", "Would you like..."), no meta-commentary. Start immediately with the answer. Always respond in Markdown format.';

export const SYSTEM_PROMPTS = {
  /** Default system prompt - reads from REACT_APP_DEFAULT_SYSTEM_PROMPT env var with fallback */
  DEFAULT: ENV_SYSTEM_PROMPT || FALLBACK_SYSTEM_PROMPT,
  /** Fallback prompt when env is not set (for reference) */
  FALLBACK: FALLBACK_SYSTEM_PROMPT,
} as const;

// Validation patterns
export const VALIDATION = {
  API_KEY_PATTERNS: {
    OPENAI: /^sk-[a-zA-Z0-9]{32,}$/,
    GOOGLE: /^[a-zA-Z0-9_-]{39}$/,
  },
  MIN_PROMPT_LENGTH: 1,
  MAX_NODE_NAME_LENGTH: 50,
} as const;

// UI Dimensions (keep in sync with CSS variables)
export const UI_DIMENSIONS = {
  NODE_MIN_WIDTH: 320, // Must match --node-min-width in CSS
  NODE_MIN_HEIGHT: 352, // Must match --node-min-height in CSS
} as const;

// Default values
export const DEFAULTS = {
  TEMPERATURE: 0.7,
  NODE_WIDTH: 300,
  NODE_HEIGHT: 'auto' as const,
  ZOOM_LEVEL: 1,
} as const;

const constants = {
  TIMING,
  LIMITS,
  MODELS,
  API_ERRORS,
  NODE_TYPES,
  DEFAULT_POSITIONS,
  CSS_CLASSES,
  SYSTEM_PROMPTS,
  VALIDATION,
  UI_DIMENSIONS,
  DEFAULTS,
} as const;

export default constants;
